import React, { useState, useEffect, useMemo, useCallback } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
	ArrowLeft,
	Trash2,
	FolderOpen,
	AlertCircle,
	Users,
	Mail,
	CheckCircle,
	Award,
	LayoutDashboard,
	FileText,
	CheckSquare,
	Activity,
	Files,
} from 'lucide-react'
import { Button, Alert, ConfirmDialog, Tabs } from '@components/common'
import { ROLES, hasPermission } from '@config/roles'
import authService from '@services/authService'
import { getUserRole } from '@utils/roleMapper'
import projectService from '@services/projectService'
import checklistService from '@services/checklistService'
import projectDocumentService from '@services/projectDocumentService'
import certificateService from '@services/certificateService'
import projectApprovalService from '@services/projectApprovalService'
import projectTeamService from '@services/projectTeamService'

// Sub-components
import ProjectHeader from './components/ProjectHeader'

// Tab components
import {
	OverviewTab,
	DetailsTab,
	ChecklistsTab,
	DocumentsTab,
	TeamTab,
	ActivityTab,
} from './components/tabs'

// Approval components
import {
	ApprovalTimeline,
	PMReviewModal,
	ExecutiveReviewModal,
} from '@components/features/projects'

// Team Management
import TeamManagementModal from '@components/features/projects/TeamManagementModal'

import './ProjectDetail.scss'

/**
 * Project Detail Page Component
 *
 * Displays comprehensive project information in a tabbed interface:
 * - Overview: Progress analytics, stats, approval timeline, latest updates
 * - Details: Project info with editable blocks
 * - Checklists: Grouped by certificate with task navigation
 * - Team: Role-based team member management
 * - Recent Activity: Complete project history
 */
const ProjectDetail = () => {
	const { id } = useParams()
	const navigate = useNavigate()

	// Get authenticated user and their role from authService
	const user = authService.getCurrentUser()
	const userRole = getUserRole(user) || ROLES.COMPANY_ADMIN

	// State
	const [project, setProject] = useState(null)
	const [certificates, setCertificates] = useState([])
	const [checklists, setChecklists] = useState([])
	const [documents, setDocuments] = useState([])
	const [teamMembers, setTeamMembers] = useState([])
	const [activities, setActivities] = useState([])
	const [loading, setLoading] = useState(true)
	const [error, setError] = useState(null)
	const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
	const [actionLoading, setActionLoading] = useState(false)

	// Active tab state
	const [activeTab, setActiveTab] = useState('overview')

	// Approval workflow modals
	const [showPMReviewModal, setShowPMReviewModal] = useState(false)
	const [showExecutiveReviewModal, setShowExecutiveReviewModal] = useState(false)
	const [showSubmitForApprovalConfirm, setShowSubmitForApprovalConfirm] = useState(false)

	// Team management modal
	const [showTeamManagementModal, setShowTeamManagementModal] = useState(false)

	// Permissions
	const canEditProject = hasPermission(userRole, 'canManageProjects') || hasPermission(userRole, 'canManageAssignedProjects')
	const canDeleteProject = hasPermission(userRole, 'canManageProjects')
	const canManageTeam = hasPermission(userRole, 'canManageProjects')
	const canAddChecklist = userRole === ROLES.COMPLIANCE_MANAGER
	const canApproveChecklist = [ROLES.COMPANY_ADMIN, ROLES.SUPER_ADMIN].includes(userRole)
	const canPmReviewChecklist = userRole === ROLES.PROJECT_MANAGER
	const canManagementReviewChecklist = [ROLES.MANAGEMENT, ROLES.EXECUTIVE, ROLES.COMPANY_ADMIN, ROLES.SUPER_ADMIN].includes(userRole)
	const canRevokeChecklist = [ROLES.MANAGEMENT, ROLES.EXECUTIVE, ROLES.COMPANY_ADMIN, ROLES.SUPER_ADMIN].includes(userRole)
	// Note: canManageDocuments is available for future document management features
	const canManageDocuments = hasPermission(userRole, 'canManageProjects') || hasPermission(userRole, 'canManageAssignedProjects') // eslint-disable-line no-unused-vars

	// Fetch project data
	useEffect(() => {
		if (id) {
			fetchProjectData()
		}
	}, [id])

	const fetchProjectData = async () => {
		setLoading(true)
		setError(null)

		try {
			// Fetch project details
			const projectResponse = await projectService.getById(id)
			let projectData = null
			if (projectResponse.success || projectResponse.data) {
				projectData = projectResponse.data || projectResponse
				setProject(projectData)
			} else {
				setError('Failed to load project details')
				return
			}

			// Fetch certificates details if project has certificate IDs
			try {
				if (projectData.certificates) {
					let certIds = projectData.certificates
					if (typeof certIds === 'string') {
						certIds = JSON.parse(certIds)
					}
					if (Array.isArray(certIds) && certIds.length > 0) {
						const certPromises = certIds.map((certId) => certificateService.getById(certId))
						const certResponses = await Promise.all(certPromises)
						const certData = certResponses
							.map((res) => res.data || res)
							.filter((cert) => cert && cert.id)
						setCertificates(certData)
					}
				}
			} catch (err) {
				console.warn('Could not load certificates:', err)
				setCertificates([])
			}

			// Fetch checklists
			try {
				const checklistResponse = await checklistService.getByProject(id)
				if (checklistResponse.success || checklistResponse.data) {
					setChecklists(checklistResponse.data || checklistResponse || [])
				}
			} catch (err) {
				console.warn('Could not load checklists:', err)
				setChecklists([])
			}

			// Fetch documents
			try {
				const docsResponse = await projectDocumentService.getByProject(id)
				if (docsResponse.success || docsResponse.data) {
					setDocuments(docsResponse.data || docsResponse || [])
				}
			} catch (err) {
				console.warn('Could not load documents:', err)
				setDocuments([])
			}

			// Fetch team members
			try {
				const teamResponse = await projectTeamService.getTeamMembers(id)
				setTeamMembers(Array.isArray(teamResponse) ? teamResponse : [])
			} catch (err) {
				console.warn('Could not load team members:', err)
				setTeamMembers([])
			}

			// Mock activities for now
			setActivities(generateMockActivities())
		} catch (err) {
			console.error('Error fetching project:', err)
			setError('Failed to load project. Please try again.')
		} finally {
			setLoading(false)
		}
	}

	// Generate mock activities
	const generateMockActivities = () => {
		return [
			{
				id: 1,
				type: 'status_change',
				message: 'Project status changed to In Progress',
				user: 'John Doe',
				timestamp: new Date(Date.now() - 1000 * 60 * 30).toISOString(),
			},
			{
				id: 2,
				type: 'checklist_update',
				message: 'Completed 5 checklist items',
				user: 'Jane Smith',
				timestamp: new Date(Date.now() - 1000 * 60 * 60 * 2).toISOString(),
			},
			{
				id: 3,
				type: 'document_upload',
				message: 'Uploaded security policy document',
				user: 'Mike Johnson',
				timestamp: new Date(Date.now() - 1000 * 60 * 60 * 24).toISOString(),
			},
			{
				id: 4,
				type: 'team_update',
				message: 'Added 2 new team members',
				user: 'Admin',
				timestamp: new Date(Date.now() - 1000 * 60 * 60 * 48).toISOString(),
			},
			{
				id: 5,
				type: 'project_update',
				message: 'Updated project timeline and target date',
				user: 'Sarah Wilson',
				timestamp: new Date(Date.now() - 1000 * 60 * 60 * 72).toISOString(),
			},
		]
	}

	// Calculate stats
	const stats = useMemo(() => {
		const totalItems = checklists.length
		const completedItems = checklists.filter((item) => ['approved', 'not_applicable'].includes(item.workflow_status)).length
		const inProgressItems = checklists.filter((item) => item.workflow_status === 'in_progress').length
		const pendingReviewItems = checklists.filter((item) => item.workflow_status === 'pending_review').length

		let progress = 0
		if (totalItems > 0) {
			progress = Math.round((completedItems / totalItems) * 100)
		} else if (project?.progress) {
			progress = parseInt(project.progress, 10) || 0
		}

		return {
			totalItems,
			completedItems,
			inProgressItems,
			pendingReviewItems,
			progress,
			documentsCount: documents.length,
			teamCount: teamMembers.length,
		}
	}, [checklists, documents, teamMembers, project])

	// Handlers
	const handleBack = () => {
		navigate('/projects')
	}

	const handleDeleteClick = () => {
		setShowDeleteConfirm(true)
	}

	const handleDeleteConfirm = async () => {
		setActionLoading(true)
		try {
			await projectService.delete(id)
			navigate('/projects')
		} catch (err) {
			console.error('Error deleting project:', err)
			setError('Failed to delete project. Please try again.')
		} finally {
			setActionLoading(false)
			setShowDeleteConfirm(false)
		}
	}

	const handleRefresh = () => {
		fetchProjectData()
	}

	// Tab navigation handler
	const handleNavigateToTab = useCallback((tabId) => {
		setActiveTab(tabId)
	}, [])

	// Project update handler
	const handleUpdateProject = async (updateData) => {
		try {
			await projectService.update(id, updateData)
			await fetchProjectData()
		} catch (err) {
			console.error('Error updating project:', err)
			throw err
		}
	}

	// Approval workflow handlers
	const handleSubmitForApprovalClick = () => {
		setShowSubmitForApprovalConfirm(true)
	}

	const handleSubmitForApprovalConfirm = async () => {
		try {
			setActionLoading(true)
			setError(null)
			await projectApprovalService.submitForApproval(id)
			setShowSubmitForApprovalConfirm(false)
			await fetchProjectData()
		} catch (err) {
			console.error('Failed to submit for approval:', err)
			const errorMessage = err.response?.data?.message || err.message || 'Failed to submit project for approval'
			setError(errorMessage)
		} finally {
			setActionLoading(false)
		}
	}

	const handlePMReviewSuccess = async () => {
		await fetchProjectData()
	}

	const handleExecutiveReviewSuccess = async () => {
		await fetchProjectData()
	}

	// Team management handlers
	const handleManageTeamClick = () => {
		setShowTeamManagementModal(true)
	}

	const handleTeamManagementSuccess = async (updatedTeamMembers) => {
		setTeamMembers(updatedTeamMembers || [])
		await fetchProjectData()
	}

	const handleRemoveTeamMember = async (memberId) => {
		if (!window.confirm('Are you sure you want to remove this team member from the project?')) {
			return
		}
		try {
			await projectTeamService.removeMember(id, memberId)
			await fetchProjectData()
		} catch (err) {
			console.error('Error removing team member:', err)
			setError('Failed to remove team member.')
		}
	}

	// Checklist handlers
	const handleChecklistUpdate = async (itemId, data) => {
		try {
			await checklistService.update(itemId, data)
			const checklistResponse = await checklistService.getByProject(id)
			if (checklistResponse.success || checklistResponse.data) {
				setChecklists(checklistResponse.data || checklistResponse || [])
			}
		} catch (err) {
			console.error('Error updating checklist item:', err)
			setError('Failed to update checklist item.')
		}
	}

	// Create approval timeline component for Overview tab
	const approvalTimelineComponent = project && (
		<div className="overview-tab__approval-section">
			<ApprovalTimeline projectId={parseInt(id)} approvalStatus={project.approval_status || 'draft'}>
				{project.approval_status !== 'active' && (
					<>
						{(project.approval_status || 'draft') === 'draft' && !project.assigned_to && (
							<div className="approval-action-warning">
								<div className="approval-action-warning__icon">
									<AlertCircle size={20} />
								</div>
								<div className="approval-action-warning__content">
									<strong>Team Assignment Required</strong>
									<span>Assign a Project Manager before submitting for approval.</span>
								</div>
								{canManageTeam && (
									<Button variant="primary" size="small" onClick={handleManageTeamClick}>
										<Users size={16} />
										Assign Team
									</Button>
								)}
							</div>
						)}

						{userRole === ROLES.COMPLIANCE_MANAGER &&
							(project.approval_status || 'draft') === 'draft' &&
							project.assigned_to && (
								<div className="approval-action-button">
									<Button variant="primary" onClick={handleSubmitForApprovalClick} disabled={actionLoading}>
										<Mail size={18} />
										Send for PM Review
									</Button>
								</div>
							)}

						{userRole === ROLES.PROJECT_MANAGER && project.approval_status === 'pending_pm_review' && (
							<div className="approval-action-button">
								<Button variant="primary" onClick={() => setShowPMReviewModal(true)}>
									<CheckCircle size={18} />
									Review Checklists ({checklists.length})
								</Button>
							</div>
						)}

						{(userRole === ROLES.EXECUTIVE || userRole === ROLES.MANAGEMENT || userRole === ROLES.SUPER_ADMIN) &&
							project.approval_status === 'pending_executive_review' && (
								<div className="approval-action-button">
									<Button variant="primary" onClick={() => setShowExecutiveReviewModal(true)}>
										<Award size={18} />
										Review Excused Items
									</Button>
								</div>
							)}
					</>
				)}
			</ApprovalTimeline>
		</div>
	)

	// Define tabs configuration
	const tabs = [
		{
			id: 'overview',
			label: 'Overview',
			icon: LayoutDashboard,
			content: (
				<OverviewTab
					project={project}
					stats={stats}
					checklists={checklists}
					activities={activities}
					teamMembers={teamMembers}
					onNavigateToTab={handleNavigateToTab}
					approvalTimeline={approvalTimelineComponent}
				/>
			),
		},
		{
			id: 'details',
			label: 'Details',
			icon: FileText,
			content: (
				<DetailsTab
					project={project}
					certificates={certificates}
					documents={documents}
					canEdit={canEditProject}
					onUpdateProject={handleUpdateProject}
					onRefreshDocuments={fetchProjectData}
				/>
			),
		},
		{
			id: 'checklists',
			label: 'Checklists',
			icon: CheckSquare,
			badge: stats.totalItems,
			badgeVariant: 'primary',
			content: (
				<ChecklistsTab
					checklists={checklists}
					project={project}
					certificates={certificates}
					onItemUpdate={handleChecklistUpdate}
					canEdit={canEditProject}
					canAddChecklist={canAddChecklist}
					canApproveChecklist={canApproveChecklist}
					canPmReviewChecklist={canPmReviewChecklist}
					canManagementReviewChecklist={canManagementReviewChecklist}
					canRevokeChecklist={canRevokeChecklist}
					onRefresh={handleRefresh}
				/>
			),
		},
		{
			id: 'documents',
			label: 'Documents',
			icon: Files,
			badge: stats.documentsCount > 0 ? stats.documentsCount : undefined,
			content: <DocumentsTab projectId={id} />,
		},
		{
			id: 'team',
			label: 'Team',
			icon: Users,
			badge: stats.teamCount,
			content: (
				<TeamTab
					members={teamMembers}
					canManage={canManageTeam}
					projectId={id}
					onManageClick={handleManageTeamClick}
					onRemoveMember={handleRemoveTeamMember}
				/>
			),
		},
		{
			id: 'activity',
			label: 'Recent Activity',
			icon: Activity,
			content: <ActivityTab activities={activities} onRefresh={handleRefresh} />,
		},
	]

	// Loading state
	if (loading) {
		return (
			<div className="project-detail-page">
				<div className="project-detail-loading">
					<div className="loading-spinner"></div>
					<p>Loading project details...</p>
				</div>
			</div>
		)
	}

	// Error state
	if (error && !project) {
		return (
			<div className="project-detail-page">
				<div className="project-detail-error">
					<AlertCircle size={48} />
					<h3>Error Loading Project</h3>
					<p>{error}</p>
					<Button variant="outline" onClick={handleBack}>
						<ArrowLeft size={18} />
						Back to Projects
					</Button>
				</div>
			</div>
		)
	}

	// Not found state
	if (!project) {
		return (
			<div className="project-detail-page">
				<div className="project-detail-error">
					<FolderOpen size={48} />
					<h3>Project Not Found</h3>
					<p>The project you're looking for doesn't exist or has been deleted.</p>
					<Button variant="outline" onClick={handleBack}>
						<ArrowLeft size={18} />
						Back to Projects
					</Button>
				</div>
			</div>
		)
	}

	return (
		<div className="project-detail-page">
			{/* Error Alert */}
			{error && (
				<Alert type="error" onClose={() => setError(null)} className="project-detail-page__alert">
					{error}
				</Alert>
			)}

			{/* Project Header */}
			<ProjectHeader
				project={project}
				onBack={handleBack}
				onDelete={canDeleteProject ? handleDeleteClick : null}
				onRefresh={handleRefresh}
			/>

			{/* Tabbed Content */}
			<div className="project-detail-tabs">
				<Tabs
					tabs={tabs}
					activeTab={activeTab}
					onTabChange={setActiveTab}
					variant="underline"
					size="medium"
				/>
			</div>

			{/* Delete Confirmation Dialog */}
			<ConfirmDialog
				isOpen={showDeleteConfirm}
				onClose={() => setShowDeleteConfirm(false)}
				onConfirm={handleDeleteConfirm}
				title="Delete Project"
				message={`Are you sure you want to delete "${project?.name}"? This action cannot be undone and will delete all associated checklists and documents.`}
				confirmLabel="Delete"
				confirmVariant="danger"
				loading={actionLoading}
			/>

			{/* Submit for PM Review Confirmation Dialog */}
			<ConfirmDialog
				isOpen={showSubmitForApprovalConfirm}
				onClose={() => setShowSubmitForApprovalConfirm(false)}
				onConfirm={handleSubmitForApprovalConfirm}
				title="Submit for PM Review"
				message={`Are you sure you want to submit "${project?.name}" for Project Manager review? The assigned PM will be notified to review all checklists.`}
				confirmLabel="Submit for Review"
				confirmVariant="primary"
				loading={actionLoading}
			/>

			{/* PM Review Modal */}
			<PMReviewModal
				isOpen={showPMReviewModal}
				onClose={() => setShowPMReviewModal(false)}
				projectId={parseInt(id)}
				checklists={checklists}
				onSuccess={handlePMReviewSuccess}
			/>

			{/* Executive Review Modal */}
			<ExecutiveReviewModal
				isOpen={showExecutiveReviewModal}
				onClose={() => setShowExecutiveReviewModal(false)}
				projectId={parseInt(id)}
				onSuccess={handleExecutiveReviewSuccess}
			/>

			{/* Team Management Modal */}
			<TeamManagementModal
				isOpen={showTeamManagementModal}
				onClose={() => setShowTeamManagementModal(false)}
				projectId={parseInt(id)}
				onSuccess={handleTeamManagementSuccess}
			/>
		</div>
	)
}

export default ProjectDetail
